import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/lib/adminApi';
import { feedbackApi } from '@/lib/feedbackApi';
import { filterInstruments, type Instrument } from '@/lib/instruments';
import { AdminPage, RegistryTable, Pill, FeedbackControl } from '@/admin';

const REGISTRY = 'instruments';
const CATEGORY_HEX: Record<string, string> = {
  safety_intake: '#F2635A', vitals_intake: '#2BBAA8', lifestyle_intake: '#F2A93B',
  symptom: '#D94080', screening: '#5B72E8',
};

export function InstrumentsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['instruments'], queryFn: () => adminApi.instruments.list() });
  const fb = useQuery({ queryKey: ['feedback-summary', REGISTRY], queryFn: () => feedbackApi.summary(REGISTRY) });
  const [search, setSearch] = useState('');

  const all = q.data?.items ?? [];
  const rows = filterInstruments(all, search);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['feedback-summary', REGISTRY] });

  return (
    <AdminPage title="Instrument Registry" description="Reusable structured self-report modules — each produces data that satisfies one or more panels. Read-only (authored via seed).">
      {q.data ? <div className="mb-13 text-body-sm text-text-secondary">{all.length} instruments · registry v{String(q.data.version ?? '?')}{q.data.last_updated ? ` · ${q.data.last_updated}` : ''}</div> : null}
      <RegistryTable<Instrument>
        columns={[
          { key: 'item_id', header: 'Item ID', className: 'font-mono text-[11px]', render: (i) => i.item_id },
          { key: 'name', header: 'Canonical name', className: 'font-medium', render: (i) => i.canonical_name },
          { key: 'category', header: 'Category', render: (i) => i.category ? <Pill label={i.category} color={CATEGORY_HEX[i.category]} /> : <span className="text-text-muted text-[12px]">—</span> },
          { key: 'scale', header: 'Scale', className: 'font-mono text-[11px] text-text-secondary', render: (i) => i.scale || '—' },
          { key: 'description', header: 'Description', className: 'max-w-[420px] text-[12px] text-text-secondary', render: (i) => i.description ? (i.description.length > 180 ? `${i.description.slice(0, 179)}…` : i.description) : '—' },
        ]}
        rows={rows}
        getRowKey={(i) => i.item_id}
        count={all.length}
        state={{ loading: q.isLoading, error: q.isError }}
        emptyText={search ? `No instruments match "${search}"` : 'No instruments.'}
        search={{ value: search, onChange: setSearch, placeholder: 'Search by id, name, category, or scale…' }}
        rowActions={(i) => <FeedbackControl registry={REGISTRY} itemId={i.item_id} itemLabel={i.canonical_name} openCount={fb.data?.[i.item_id]?.open ?? 0} onChanged={refresh} />}
      />
    </AdminPage>
  );
}
